import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";

import Select from "react-select";
import * as yup from "yup";
import { useFormik } from "formik";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import axios from 'axios';
import Cookies from 'js-cookie';

import Breadcrumbs from "../../components/Common/Breadcrumb";

import { getPaketByID } from '../../services/paketService';
import { listBidangs } from '../../services/bidangService';
import { fetchTryout, createTryout, updateTryout, deleteTryout, getTryoutById } from '../../services/tryoutService';

const BASE_URL_BE = process.env.REACT_APP_BASE_URL_BE;

const getAuthToken = () => Cookies.get('access_token');

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getAuthToken()}`
});

const TryoutAddForm = () => {
  document.title = "Add Tryout";

  const [selectedFile, setSelectedFile] = useState(null);
  const [tryouts, setTryouts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [loadingTryouts, setLoadingTryouts] = useState(false);
  const [paketOptions, setPaketOptions] = useState([]);
  const [loadingPaket, setLoadingPaket] = useState(true);
  const [bidangOptions, setBidangOptions] = useState([]);
  const [loadingBidang, setLoadingBidang] = useState(true);
  const [editingTryoutId, setEditingTryoutId] = useState(null);
  const [jenisSeleksi, setJenisSeleksi] = useState(null);
  const { id: paketIdFromParams } = useParams();
  const [deleteModal, setDeleteModal] = useState(false);
  const [tryoutToDelete, setTryoutToDelete] = useState(null);

  const handleDeleteClick = (tryout) => {
    setTryoutToDelete(tryout);
    setDeleteModal(true);
  };

  const [initialJenisSeleksi, setInitialJenisSeleksi] = useState("");

  const fetchTryouts = async (page = 1, filterParams = {}) => {
    try {
      setLoadingTryouts(true);
      const currentJenisSeleksi = initialJenisSeleksi || jenisSeleksi;
      // Add id_paket to the filters
      const filters = { ...filterParams, jenis_seleksi: currentJenisSeleksi, id_paket: paketIdFromParams };
      const response = await fetchTryout(page, pagination.per_page, filters);

      if (!response || !response.success) {
        throw new Error(response?.message || "No response from server");
      }

      const tryoutData = response.data?.data || response.data || [];
      const metaData = response.meta || {
        page: 1,
        per_page: pagination.per_page,
        total: tryoutData.length
      };

      setTryouts(Array.isArray(tryoutData) ? tryoutData : [tryoutData]);
      setPagination({
        page: metaData.page,
        per_page: metaData.per_page,
        total: metaData.total
      });

    } catch (error) {
      console.error("Error fetching tryouts:", error);
      setTryouts([]);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0
      }));
      toast.error(error.message || "Error loading tryout data");
    } finally {
      setLoadingTryouts(false);
    }
  };

  useEffect(() => {
    if (initialJenisSeleksi && paketIdFromParams) { // Ensure paketIdFromParams is available
      fetchTryouts(pagination.page);
    }
  }, [pagination.page, pagination.per_page, initialJenisSeleksi, paketIdFromParams]); // Add paketIdFromParams to dependencies

  useEffect(() => {
    const fetchPaketOptions = async () => {
      try {
        // You already have paketIdFromParams from useParams, no need to parse pathname again
        const id = paketIdFromParams;

        if (isNaN(id)) {
          console.error("Invalid ID in URL, getPaketByID skipped.");
          toast.warn("ID Paket tidak ditemukan di URL. Tidak dapat memuat data paket.");
          setLoadingPaket(false);
          return;
        }

        const response = await getPaketByID(id);
        const paket = response.data?.data;

        if (paket?.jenis_seleksi) {
          setPaketOptions([{
            value: paket.id_paket,
            label: paket.jenis_seleksi
          }]);
          formik.setFieldValue("jenis_seleksi", paket.jenis_seleksi);
          formik.setFieldValue("id_paket", paket.id_paket);
          setInitialJenisSeleksi(paket.jenis_seleksi);
          setJenisSeleksi(paket.jenis_seleksi);
        } else {
          console.warn("Paket not found or data structure mismatch:", paket);
          toast.warn("Data paket tidak lengkap atau tidak ditemukan.");
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          toast.error("ID Paket Tidak Ditemukan!", {
            autoClose: 2000,
            onClose: () => window.location.href = "/paket-list",
          });
        } else {
          toast.error(error.message || "Error fetching paket data.");
          console.error("Error:", error);
        }
      } finally {
        setLoadingPaket(false);
      }
    };

    fetchPaketOptions();
  }, [paketIdFromParams]);

  useEffect(() => {
    const fetchBidangOptions = async () => {
      try {
        const response = await listBidangs();

        if (response && response.success && response.data) {
          const filteredData = jenisSeleksi
            ? response.data.filter(bidang => bidang.jenis_seleksi === jenisSeleksi)
            : response.data;

          const groupedOptions = filteredData.reduce((acc, bidang) => {
            const group = acc.find(g => g.label === bidang.jenis_seleksi);
            if (group) {
              group.options.push({
                value: bidang.id_bidang,
                label: bidang.nama_bidang
              });
            } else {
              acc.push({
                label: bidang.jenis_seleksi,
                options: [{
                  value: bidang.id_bidang,
                  label: bidang.nama_bidang
                }]
              });
            }
            return acc;
          }, []);

          setBidangOptions(groupedOptions);
        } else {
          throw new Error(response.message || "Invalid bidang data structure");
        }
      } catch (error) {
        console.error("Error fetching bidang data:", error);
        toast.error(error.message || "Failed to load bidang data");
      } finally {
        setLoadingBidang(false);
      }
    };

    fetchBidangOptions();
  }, [jenisSeleksi]);

  const tipeOptions = [
    { value: "GRATIS", label: "Gratis" },
    { value: "PREMIUM", label: "Premium" },
  ];

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const formik = useFormik({
    initialValues: {
      id_paket: parseInt(paketIdFromParams) || "",
      nama_tryout: "",
      tipe: "",
      durasi_menit: "",
      id_bidang: "",
      jenis_seleksi: initialJenisSeleksi,
    },
    validationSchema: yup.object().shape({
      id_paket: yup.number().required("Paket harus dipilih"),
      nama_tryout: yup.string().required("Nama Tryout harus diisi"),
      tipe: yup.string().required("Tipe Tryout harus dipilih"),
      durasi_menit: yup
        .number()
        .required("Durasi harus diisi")
        .min(1, "Durasi minimal 1 menit"),
      // jumlah_soal dihapus dari validasi
      id_bidang: yup.number().required("Bidang harus dipilih"),
    }),
    onSubmit: async (values) => {
      console.log("Formik onSubmit triggered. Values:", values);
      try {
        const formData = new FormData();

        Object.keys(values).forEach(key => {
          if (values[key] !== undefined && values[key] !== null) {
            formData.append(key, values[key]);
          }
        });

        if (selectedFile) {
          formData.append("gambar", selectedFile);
        } else if (editingTryoutId) {
          formData.append("gambar", "");
        }

        for (let [key, value] of formData.entries()) {
            console.log(`${key}: ${value}`);
        }

        console.log("Submitting Tryout Data:", Object.fromEntries(formData));

        let response;
        if (editingTryoutId) {
          console.log("Updating Tryout with ID:", editingTryoutId);
          response = await updateTryout(editingTryoutId, formData);
          toast.success("sub paket berhasil diperbarui!");
        } else {
          console.log("Creating New Tryout.");
          response = await createTryout(formData);
          toast.success("sub paket berhasil ditambahkan!");
        }

        console.log("API Response after submit:", response);

        await fetchTryouts(pagination.page);

        formik.resetForm();
        setSelectedFile(null);
        setEditingTryoutId(null);
        formik.setFieldValue("id_paket", parseInt(paketIdFromParams));
        formik.setFieldValue("jenis_seleksi", initialJenisSeleksi);

      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(error.response?.data?.message || "Gagal menyimpan sub paket.");
      }
    },
  });

  const handleEdit = async (id_tryout) => {
    try {
      const response = await getTryoutById(id_tryout);
      const tryoutData = response.data?.data || response.data;

      if (!tryoutData) {
        throw new Error("Data tryout tidak ditemukan");
      }

      formik.setValues({
        nama_tryout: tryoutData.nama_tryout || '',
        tipe: tryoutData.tipe || '',
        durasi_menit: tryoutData.durasi_menit || '',
        // jumlah_soal dihapus dari setValues
        id_bidang: tryoutData.id_bidang || '',
        id_paket: tryoutData.id_paket || parseInt(paketIdFromParams),
      });

      setEditingTryoutId(id_tryout);
      setSelectedFile(null);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Gagal memuat data tryout");
    }
  };
  const handleDelete = async (id_tryout) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus tryout ini?")) {
      try {
        await deleteTryout(id_tryout);
        await fetchTryouts(pagination.page);
        toast.success("Tryout berhasil dihapus!");
      } catch (error) {
        console.error("Error deleting tryout:", error);
        toast.error(error.response?.data?.message || "Gagal menghapus tryout.");
      }
    }
  };


  const handleCancelEdit = () => {
    formik.resetForm();
    setSelectedFile(null);
    setEditingTryoutId(null);

    formik.setFieldValue("id_paket", parseInt(paketIdFromParams));
    formik.setFieldValue("jenis_seleksi", initialJenisSeleksi);

    toast.info("Mode pengeditan dibatalkan.");
  };



  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Tryout" breadcrumbItem="Tambah Sub Paket" />

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <CardTitle>Informasi Sub Paket</CardTitle>
                  <p className="card-title-desc mb-4">
                    Isi semua informasi dibawah ini
                  </p>

                  <Form onSubmit={formik.handleSubmit} autoComplete="off">
                    <Row>
                      <Col sm="6">
                        <div className="mb-3">
                          <Label htmlFor="jenis_seleksi">Jenis Seleksi</Label>
                          <Input
                            id="jenis_seleksi"
                            name="jenis_seleksi"
                            type="text"
                            placeholder="Jenis Seleksi"
                            value={formik.values.jenis_seleksi}
                            readOnly
                            style={{
                              borderColor: '#3f51b5',
                              borderWidth: '2px',
                              backgroundColor: '#f0f0f0'
                            }}
                          />
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="nama_tryout">Nama Sub Paket</Label>
                          <Input
                            id="nama_tryout"
                            name="nama_tryout"
                            type="text"
                            placeholder="Nama Sub Paket"
                            value={formik.values.nama_tryout}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.nama_tryout &&
                              formik.errors.nama_tryout
                                ? true
                                : false
                            }
                          />
                          {formik.errors.nama_tryout &&
                          formik.touched.nama_tryout ? (
                            <FormFeedback type="invalid">
                              {formik.errors.nama_tryout}
                            </FormFeedback>
                          ) : null}
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="tipe">Tipe Sub Paket</Label>
                          <Select
                            id="tipe"
                            name="tipe"
                            options={tipeOptions}
                            value={tipeOptions.find(
                              (option) => option.value === formik.values.tipe
                            )}
                            onChange={(option) =>
                              formik.setFieldValue("tipe", option.value)
                            }
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.tipe && formik.errors.tipe
                                ? "is-invalid"
                                : ""
                            }
                            placeholder="Pilih Tipe"
                          />
                          {formik.touched.tipe && formik.errors.tipe ? (
                            <div className="invalid-feedback d-block">
                              {formik.errors.tipe}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col sm="6">
                        <div className="mb-3">
                          <Label htmlFor="durasi_menit">Durasi (menit)</Label>
                          <Input
                            id="durasi_menit"
                            name="durasi_menit"
                            type="number"
                            placeholder="Durasi dalam menit"
                            value={formik.values.durasi_menit}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.durasi_menit &&
                              formik.errors.durasi_menit
                                ? true
                                : false
                            }
                          />
                          {formik.errors.durasi_menit &&
                          formik.touched.durasi_menit ? (
                            <FormFeedback type="invalid">
                              {formik.errors.durasi_menit}
                            </FormFeedback>
                          ) : null}
                        </div>
                        {/* Jumlah Soal Dihapus dari Form */}
                        <div className="mb-3">
                          <Label htmlFor="id_bidang">Bidang</Label>
                          <Select
                            id="id_bidang"
                            name="id_bidang"
                            options={bidangOptions}
                            isLoading={loadingBidang}
                            isDisabled={!jenisSeleksi || loadingBidang}
                            value={bidangOptions.flatMap(group =>
                              group.options).find(
                              option => option.value === formik.values.id_bidang
                            )}
                            onChange={(option) =>
                              formik.setFieldValue("id_bidang", option.value)
                            }
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.id_bidang && formik.errors.id_bidang
                                ? "is-invalid"
                                : ""
                            }
                            placeholder={loadingBidang ? "Memuat data bidang..." : (jenisSeleksi ? "Pilih Bidang" : "Pilih paket terlebih dahulu")}
                            noOptionsMessage={() => jenisSeleksi ? "Tidak ada bidang yang tersedia" : "Pilih paket terlebih dahulu"}
                          />
                          {formik.touched.id_bidang && formik.errors.id_bidang ? (
                            <div className="invalid-feedback d-block">
                              {formik.errors.id_bidang}
                            </div>
                          ) : null}
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <Label htmlFor="gambar">Gambar Sub Paket</Label>
                      <Input
                        id="gambar"
                        name="gambar"
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                      {selectedFile && (
                        <div className="mt-2">
                          <span>File dipilih: {selectedFile.name}</span>
                          <Button
                            color="danger"
                            size="sm"
                            className="ms-2"
                            onClick={() => setSelectedFile(null)}
                          >
                            Hapus
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" color="primary">
                        {editingTryoutId ? "Update" : "Simpan"}
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={handleCancelEdit}
                      >
                        {editingTryoutId ? "Batal Edit" : "Batal"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <CardTitle>Daftar Sub Paket</CardTitle>

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Label htmlFor="perPageSelect" className="mb-0">Items per page:</Label>
                    <Input
                      id="perPageSelect"
                      type="select"
                      style={{ width: '80px' }}
                      value={pagination.per_page}
                      onChange={(e) => setPagination(prev => ({
                        ...prev,
                        per_page: Number(e.target.value),
                        page: 1
                      }))}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </Input>
                  </div>

                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Jenis Seleksi</th>
                          <th>Nama Sub Paket</th>
                          <th>Tipe</th>
                          <th>Durasi (menit)</th>
                          {/* <th>Jumlah Soal</th> Ini juga dihapus dari header tabel */}
                          <th>Bidang</th>
                          <th>Tanggal Dibuat</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingTryouts ? (
                          <tr>
                            <td colSpan="8" className="text-center"> {/* Colspan disesuaikan */}
                              <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : tryouts.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center"> {/* Colspan disesuaikan */}
                              Not Found
                            </td>
                          </tr>
                        ) : (
                          tryouts.map((tryout, index) => (
                            <tr key={tryout.id_tryout}>
                              <th scope="row">{(pagination.page - 1) * pagination.per_page + index + 1}</th>
                              <td>{tryout.jenis_seleksi}</td>
                              <td>{tryout.nama_tryout}</td>
                              <td>
                                <span
                                  className={`badge ${tryout.tipe === 'GRATIS' ? 'bg-success' : 'bg-primary'}`}
                                >
                                  {tryout.tipe}
                                </span>
                              </td>
                              <td>{tryout.durasi_menit}</td>
                              {/* <td>{tryout.jumlah_soal}</td> Ini juga dihapus dari baris tabel */}
                              <td>{tryout.nama_bidang}</td>
                              <td>{new Date(tryout.created_at).toISOString().slice(0, 10)}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => handleEdit(tryout.id_tryout)}
                                  >
                                    Edit
                                  </Button>
                                      <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleDeleteClick(tryout)}
                                    >
                                      Hapus
                                    </Button>

                                <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                                  <ModalHeader toggle={() => setDeleteModal(false)}>Konfirmasi Hapus</ModalHeader>
                                  <ModalBody>
                                    Apakah Anda yakin ingin menghapus tryout "{tryoutToDelete?.nama_tryout}"?
                                  </ModalBody>
                                  <ModalFooter>
                                    <Button color="secondary" onClick={() => setDeleteModal(false)}>
                                      Batal
                                    </Button>
                                    <Button
                                      color="danger"
                                      onClick={async () => {
                                        try {
                                          await deleteTryout(tryoutToDelete.id_tryout);
                                          await fetchTryouts(pagination.page);
                                          toast.success("Tryout berhasil dihapus!");
                                        } catch (error) {
                                          console.error("Error deleting tryout:", error);
                                          toast.error(error.response?.data?.message || "Gagal menghapus tryout.");
                                        } finally {
                                          setDeleteModal(false);
                                          setTryoutToDelete(null);
                                        }
                                      }}
                                    >
                                      Hapus
                                    </Button>
                                  </ModalFooter>
                                </Modal>

                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <span className="text-muted">
                        Showing {(pagination.page - 1) * pagination.per_page + 1} to{' '}
                        {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                        {pagination.total} entries
                      </span>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        Previous
                      </Button>

                      {Array.from({ length: Math.ceil(pagination.total / pagination.per_page) }, (_, i) => i + 1)
                        .slice(
                          Math.max(0, pagination.page - 3),
                          Math.min(Math.ceil(pagination.total / pagination.per_page), pagination.page + 2)
                        )
                        .map(page => (
                          <Button
                            key={page}
                            color={pagination.page === page ? "primary" : "light"}
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page }))}
                          >
                            {page}
                          </Button>
                        ))
                      }

                      <Button
                        color="primary"
                        size="sm"
                        disabled={pagination.page * pagination.per_page >= pagination.total}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default TryoutAddForm;